import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SeoProps {
  title: string;
  description: string;
  canonicalPath?: string;
  jsonLd?: Record<string, unknown>;
  /** Absolute URL or root-relative path (e.g. "/og-image.jpg"). */
  image?: string;
  imageAlt?: string;
  /** Open Graph type. Defaults to "website". Use "article" for blog posts. */
  ogType?: string;
}

const BASE_URL = "https://sprankelien.be";
const DEFAULT_IMAGE = "/og-image.jpg";
const DEFAULT_IMAGE_ALT =
  "Cinematische sfeerbeeld van Praktijk Sprankelien — een nachtelijk bos met maan, vuurvliegjes en een silhouet van een kat";

const toAbsolute = (url: string) =>
  url.startsWith("http") ? url : `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;

const PageSeo = ({
  title,
  description,
  canonicalPath,
  jsonLd,
  image,
  imageAlt,
  ogType = "website",
}: SeoProps) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonical = `${BASE_URL}${path === "/" ? "" : path}`;
  const fullTitle = path === "/" ? title : `${title} — Praktijk Sprankelien`;
  const ogImage = toAbsolute(image ?? DEFAULT_IMAGE);
  const ogImageAlt = imageAlt ?? DEFAULT_IMAGE_ALT;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);

    // Open Graph
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:site_name", "Praktijk Sprankelien", "property");
    setMeta("og:locale", "nl_BE", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:secure_url", ogImage, "property");
    setMeta("og:image:type", "image/jpeg", "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("og:image:alt", ogImageAlt, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    setMeta("twitter:image:alt", ogImageAlt);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // JSON-LD
    const scriptId = "page-jsonld";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      // cleanup JSON-LD on unmount
      document.getElementById(scriptId)?.remove();
    };
  }, [fullTitle, description, canonical, jsonLd, ogImage, ogImageAlt, ogType]);

  return null;
};

export default PageSeo;
